import React, { useState, useEffect } from "react";
import { servicesTree, localizeServicesTree, ServiceNode } from "../services/servicesConfigAdvanced";
import IntegrationsPanel from "../component/IntegrationsPanel";
import mockData from "../services/mockAppAnalytics.json";
import SidebarAdvanced from "../component/Dashboard/SidebarAdvanced";
import SidebarTree from "../component/Dashboard/SidebarTree";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { apps, MobileAppsPanel } from "../features/mobile/MobileAppsPanel";
import ComingSoon from "../component/ComingSoon";
import { translations, LanguageCode } from "../utils/translations";

/* ----------------------------
   TREE COMPONENT
-----------------------------*/

interface TreeProps {
  nodes: ServiceNode[];
  onSelect: (id: string) => void;
}

interface AppItem {
  id: string;
  name: string;
  packageName: string;
  data: typeof mockData;
}

const Tree: React.FC<TreeProps> = ({ nodes, onSelect }) => {
  const [openNodes, setOpenNodes] = useState<string[]>([]);

  const toggleNode = (id: string) => {
    setOpenNodes((prev) =>
      prev.includes(id) ? prev.filter((node) => node !== id) : [...prev, id]
    );
  };

  const renderNodes = (nodes: ServiceNode[]) => (
    <ul style={{ listStyle: "none", paddingLeft: "15px" }}>
      {nodes.map((node) => (
        <li key={node.id} style={{ marginBottom: "6px" }}>
          <div
            onClick={() => {
              if (node.children) toggleNode(node.id);
              onSelect(node.id);
            }}
            style={{
              cursor: "pointer",
              padding: "6px 10px",
              borderRadius: "6px",
              background: "#F4F6FA",
              fontWeight: 500,
              fontSize: "14px",
              color: "#333",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#F4F6FA")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            {node.label}
            {node.children && (openNodes.includes(node.id) ? " −" : " +")}
          </div>

          {node.children &&
            openNodes.includes(node.id) &&
            renderNodes(node.children)}
        </li>
      ))}
    </ul>
  );

  return renderNodes(nodes);
};

/* ----------------------------
   KPI CARD
-----------------------------*/

/* ----------------------------
   STYLES
-----------------------------*/

const selectStyle: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #ddd",
  background: "#fff",
};

const boxStyle: React.CSSProperties = {
  flex: 1,
  background: "#F4F6FA",
  padding: "20px",
  borderRadius: "12px",
};

/* ----------------------------
   DASHBOARD PAGE
-----------------------------*/

interface DashboardProps {
  lang?: LanguageCode;
}

const Dashboard: React.FC<DashboardProps> = ({ lang = "en" }) => {
  const [activeSection, setActiveSection] = useState<string>("overview");
  const { appSlug } = useParams<{ appSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes("/mobile/all")) {
      setActiveSection("allApps");
    } else if (location.pathname.includes("/mobile/analytics")) {
      setActiveSection("appAnalytics");
    }
  }, [location.pathname]);
  const handleSelect = (id: string) => {
    switch (id) {
      case "templateLibrary":
        navigate("/dashboard/automation/email/templates");
        break;

      case "createTemplate":
        navigate("/dashboard/automation/email/templates/create");
        break;

      case "allApps":
        navigate("/dashboard/mobile/all");
        break;

      case "allWebsites":
        navigate("/dashboard/website/all");
        break;

      case "appAnalytics":
        const defaultApp = localStorage.getItem("default_app") || "mapp1";
        navigate(`/dashboard/mobile/analytics/${defaultApp}`);
        break;
      case "integrations":
        navigate("/dashboard/integrations");
        break;
      case "facebookConnect":
        navigate("/dashboard/facebookConnect");
        break;
      case "youtubeConnect":
        navigate("/dashboard/youtubeConnect");
        break;
      case "commander":
        navigate("/dashboard/commander");
        break;
      default:
        navigate(`/dashboard/${id}`);
    }
  };

  const findNodeById = (nodes: ServiceNode[], id: string): ServiceNode | undefined => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return undefined;
  };

  const getActiveNodeStatus = () => {
    const pathParts = location.pathname.split("/").filter(Boolean);
    if (pathParts.length <= 1) return "live";

    const topId = pathParts[1];
    const node = findNodeById(servicesTree, topId);
    return node?.status || "live";
  };

  const activeStatus = getActiveNodeStatus();

  return (
    <div
      className="dashboard-layout"
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "#F8F9FF",
      }}
    >
      <SidebarAdvanced onSelect={handleSelect} lang={lang} />
      <div className="dashboard-content" style={{ flex: 1, padding: "40px" }}>
        <h1
          style={{
            color: "#714B67",
            fontSize: "28px",
            fontWeight: 800,
            marginBottom: "20px",
          }}
        >
          {translations[lang].dashboard.welcome}
        </h1>

        {activeStatus === "coming-soon" ? (
          (() => {
            const lastId = location.pathname.split("/").filter(Boolean).pop();
            const activeNode = lastId
              ? findNodeById(localizeServicesTree(servicesTree, lang), lastId)
              : undefined;
            return (
              <ComingSoon
                lang={lang}
                featureName={
                  activeNode?.label || lastId?.replace(/-/g, " ")
                }
              />
            );
          })()
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
